import {createSlice, PayloadAction, Reducer} from '@reduxjs/toolkit';

import {SliceActions} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';

export interface CollapsedTableState {
  [id: string]: boolean | undefined;
}

export const selectTableCollapsed = (state: BenchlingState, id: string): boolean | undefined =>
  state.ui.collapsedTables[id];

export const collapsedTables = createSlice({
  name: 'collapsedTables',
  initialState: {} as CollapsedTableState,
  reducers: {
    init: (state, action: PayloadAction<{id: string; isCollapsed: boolean}>) => {
      const {id, isCollapsed} = action.payload;
      state[id] ??= isCollapsed;
    },
    toggle: (state, action: PayloadAction<{id: string}>) => {
      const {id} = action.payload;
      state[id] = !state[id];
    },
    set: (state, action: PayloadAction<{id: string; isCollapsed: boolean}>) => {
      const {id, isCollapsed} = action.payload;
      state[id] = isCollapsed;
    },
    changeId: (state, action: PayloadAction<{oldId: string; newId: string}>) => {
      const {oldId, newId} = action.payload;
      state[newId] = state[oldId];
      delete state[oldId];
    },
    setAll: (state, action: PayloadAction<{ids: Array<string>; isCollapsed: boolean}>) => {
      const {ids, isCollapsed} = action.payload;
      for (const id of ids) {
        state[id] = isCollapsed;
      }
    },
    initAll: (state, action: PayloadAction<{ids: Array<string>; initCollapseState: Array<boolean>}>) => {
      const {ids, initCollapseState} = action.payload;
      if (ids.length !== initCollapseState.length) {
        throw new Error('IDs and collapse list must be the same size.');
      }
      for (let i = 0; i < ids.length; i++) {
        state[ids[i]] ??= initCollapseState[i];
      }
    },
  },
});

export type CollapsedTableAction = SliceActions<typeof collapsedTables.actions>;

export const reduceCollapsedTablesUI = collapsedTables.reducer as Reducer<
  CollapsedTableState,
  CollapsedTableAction
>;

/**
 * @deprecated Function components should prefer useDispatch and tests should avoid coupling directly to
 * redux actions.
 */
export const CollapsedTableReduxActions = {
  init: (id: string, isCollapsed: boolean): void => {
    getStore().dispatch(collapsedTables.actions.init({id, isCollapsed}));
  },
  toggle: (id: string): void => {
    getStore().dispatch(collapsedTables.actions.toggle({id}));
  },
  changeId: (oldId: string, newId: string): void => {
    getStore().dispatch(collapsedTables.actions.changeId({oldId, newId}));
  },
  set: (id: string, isCollapsed: boolean): void => {
    getStore().dispatch(collapsedTables.actions.set({id, isCollapsed}));
  },
};
